import {announce} from '@github-ui/aria-live'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useMemo} from 'react'
import {partitionFindings} from './entity-helpers'
import {FINDINGS_QUERY_KEY_PREFIX} from './use-findings'
import type {FindingResolvedReason, Finding, FindingsQueryData} from './types'

const pluralize = (count: number) => {
  return count === 1 ? 'finding' : 'findings'
}

type CacheSnapshot = Array<FindingsQueryData | undefined>
type MutationContext = {previousEntries: CacheSnapshot}

// Partial key matches all findings queries for a PR regardless of view toggle (prViewEnabled).
const findingsPartialKey = (pullRequestBasePath: string) => {
  return [FINDINGS_QUERY_KEY_PREFIX, pullRequestBasePath]
}

// Snapshots current cache entries then applies an optimistic update to matched findings.
// Cancels in-flight queries first to prevent refetch polling from overwriting the optimistic state.
// Returns the pre-update snapshot for rollback. Always pushes (even undefined) to keep
// index alignment with cache entries — rollbackCache relies on this 1:1 correspondence.
const snapshotAndUpdateCache = async (
  queryClient: ReturnType<typeof useQueryClient>,
  pullRequestBasePath: string,
  findingIds: Set<string>,
  updater: (finding: Finding) => Finding,
): Promise<CacheSnapshot> => {
  await queryClient.cancelQueries({queryKey: findingsPartialKey(pullRequestBasePath)})
  const snapshot: CacheSnapshot = []
  const entries = queryClient.getQueriesData<FindingsQueryData>({
    queryKey: findingsPartialKey(pullRequestBasePath),
  })

  for (const [key, data] of entries) {
    snapshot.push(data)
    if (!data?.payload) continue
    queryClient.setQueryData<FindingsQueryData>(key, {
      ...data,
      payload: {
        ...data.payload,
        findings: data.payload.findings.map(f => (findingIds.has(f.id) ? updater(f) : f)),
      },
    })
  }

  return snapshot
}

// Restores cache entries from a snapshot taken by snapshotAndUpdateCache.
const rollbackCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  pullRequestBasePath: string,
  snapshot: CacheSnapshot,
) => {
  const entries = queryClient.getQueriesData<FindingsQueryData>({
    queryKey: findingsPartialKey(pullRequestBasePath),
  })
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i]
    if (!entry) continue
    const [key] = entry
    const previous = snapshot[i]
    if (previous) {
      queryClient.setQueryData<FindingsQueryData>(key, previous)
    }
  }
}

interface DismissMutationVariables {
  findings: Finding[]
  resolvedReason: FindingResolvedReason
}

export const useDismissFindingsMutation = (pullRequestBasePath: string) => {
  const queryClient = useQueryClient()

  return useMutation<void, Error, DismissMutationVariables, MutationContext>({
    mutationFn: async ({findings, resolvedReason}) => {
      const ids = findings.map(f => f.id)
      const response = await verifiedFetchJSON(`${pullRequestBasePath}/code_review_findings/ignore`, {
        method: 'POST',
        body: {entity_ids: ids, resolved_reason: resolvedReason},
      })
      if (!response.ok) {
        throw new Error(`Failed to resolve ${pluralize(ids.length)}. Please try again.`)
      }
    },

    onMutate: async ({findings, resolvedReason}) => {
      const ids = new Set(findings.map(f => f.id))
      const previousEntries = await snapshotAndUpdateCache(queryClient, pullRequestBasePath, ids, f => ({
        ...f,
        resolvedAt: new Date().toISOString(),
        resolvedReason,
      }))
      return {previousEntries}
    },

    onSuccess: (_data, {findings}) => {
      announce(`${findings.length} ${pluralize(findings.length)} resolved`)
    },

    onError: (_error, _variables, context) => {
      if (context) rollbackCache(queryClient, pullRequestBasePath, context.previousEntries)
    },

    onSettled: () => {
      void queryClient.invalidateQueries({queryKey: findingsPartialKey(pullRequestBasePath)})
    },
  })
}

interface ReopenMutationVariables {
  findings: Finding[]
}

export const useReopenFindingsMutation = (pullRequestBasePath: string) => {
  const queryClient = useQueryClient()

  return useMutation<void, Error, ReopenMutationVariables, MutationContext>({
    mutationFn: async ({findings}) => {
      const ids = findings.map(f => f.id)
      const response = await verifiedFetchJSON(`${pullRequestBasePath}/code_review_findings/ignore`, {
        method: 'DELETE',
        body: {entity_ids: ids},
      })
      if (!response.ok) {
        throw new Error(`Failed to reopen ${pluralize(ids.length)}. Please try again.`)
      }
    },

    onMutate: async ({findings}) => {
      const ids = new Set(findings.map(f => f.id))
      const previousEntries = await snapshotAndUpdateCache(queryClient, pullRequestBasePath, ids, f => ({
        ...f,
        resolvedAt: null,
        resolvedReason: null,
      }))
      return {previousEntries}
    },

    onSuccess: (_data, {findings}) => {
      announce(`${findings.length} ${pluralize(findings.length)} reopened`)
    },

    onError: (_error, _variables, context) => {
      if (context) rollbackCache(queryClient, pullRequestBasePath, context.previousEntries)
    },

    onSettled: () => {
      void queryClient.invalidateQueries({queryKey: findingsPartialKey(pullRequestBasePath)})
    },
  })
}

// Convenience wrapper that composes both mutations with partitioned findings.
// Resets the opposing mutation's error state before each action so stale errors don't linger.
export const useFindingsMutations = (findings: Finding[], pullRequestBasePath: string) => {
  const {open: openFindings, ignored: ignoredFindings} = useMemo(() => partitionFindings(findings), [findings])

  const dismissMutation = useDismissFindingsMutation(pullRequestBasePath)
  const reopenMutation = useReopenFindingsMutation(pullRequestBasePath)
  const actionError = dismissMutation.error?.message ?? reopenMutation.error?.message ?? null

  const handleDismiss = useCallback(
    (findingsToDismiss: Finding[], resolvedReason: FindingResolvedReason, options?: {onSuccess?: () => void}) => {
      reopenMutation.reset()
      dismissMutation.mutate({findings: findingsToDismiss, resolvedReason}, {onSuccess: options?.onSuccess})
    },
    [dismissMutation, reopenMutation],
  )

  const handleReopen = useCallback(
    (findingsToReopen: Finding[]) => {
      dismissMutation.reset()
      reopenMutation.mutate({findings: findingsToReopen})
    },
    [dismissMutation, reopenMutation],
  )

  return {openFindings, ignoredFindings, handleDismiss, handleReopen, actionError}
}
